import MaterialSymbolsRotateLeftRounded from '@/components-v2/icons/MaterialSymbolsRotateLeftRounded';
import MaterialSymbolsRotateRightRounded from '@/components-v2/icons/MaterialSymbolsRotateRightRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslate } from '@tolgee/react';
import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { BottomModalDrawer } from '../bottom-modal-drawer';
import { getCroppedImg } from '../drag-drop-image-upload/cropImage';
import { elegantTextureBackground } from '../drag-drop-image-upload/texture-styles';
import { ModalTransitions } from '../modal-transitions';

interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropPanelProps {
  open: boolean;
  image: string;
  aspectRatio: number;
  onClose: () => void;
  onComplete: (croppedImage: string) => void;
  onLoading: (isLoading: boolean) => void;
}

// 预设宽高比选项
const ASPECT_RATIOS = [
  { label: '21:9', value: 21 / 9 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
  { label: '4:3', value: 4 / 3 },
  { label: '5:4', value: 5 / 4 },
  { label: '1:1', value: 1 / 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '3:4', value: 3 / 4 },
  { label: '2:3', value: 2 / 3 },
  { label: '9:16', value: 9 / 16 },
  { label: '9:21', value: 9 / 21 },
];

export const CropPanel = ({
  open,
  image,
  aspectRatio: initialAspectRatio,
  onClose,
  onComplete,
  onLoading,
}: CropPanelProps) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // 找到最接近的宽高比索引
  const getClosestRatioIndex = (ratio: number) => {
    let closestIndex = 0;
    let minDiff = Math.abs(ASPECT_RATIOS[0].value - ratio);

    ASPECT_RATIOS.forEach((preset, index) => {
      const diff = Math.abs(preset.value - ratio);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  // 根据初始比例计算最接近的预设比例索引
  const initialSliderPosition = getClosestRatioIndex(initialAspectRatio);

  // 选择预设宽高比的滑块位置
  const [sliderPosition, setSliderPosition] = useState(initialSliderPosition);

  // 使用来自滑块位置的预设比例，而不是直接使用initialAspectRatio
  const [aspectRatio, setAspectRatio] = useState(ASPECT_RATIOS[initialSliderPosition].value);

  // 存储裁剪区域
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  // 重置状态，当modal重新打开时
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0); // 重置旋转角度
      const closestIndex = getClosestRatioIndex(initialAspectRatio);
      setSliderPosition(closestIndex);
      setAspectRatio(ASPECT_RATIOS[closestIndex].value);
    }
  }, [open, initialAspectRatio]);

  // 当initialAspectRatio变化时重新计算最接近的预设比例
  useEffect(() => {
    const closestIndex = getClosestRatioIndex(initialAspectRatio);
    setSliderPosition(closestIndex);
    setAspectRatio(ASPECT_RATIOS[closestIndex].value);
  }, [initialAspectRatio]);

  // 当裁剪完成时，更新裁剪区域
  const onCropComplete = useCallback(
    (_croppedArea: unknown, croppedAreaPixels: CroppedAreaPixels) => {
      // 只更新裁剪区域信息，不生成预览
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [], // 不依赖任何状态，避免不必要的重新渲染
  );

  const handleCropConfirm = async () => {
    if (!image || !croppedAreaPixels) return;

    try {
      onLoading(true);

      // 直接生成裁剪结果，不使用缓存的预览
      const croppedImage = await getCroppedImg(image, croppedAreaPixels, rotation);

      // 简化的验证 - 只检查数据URL是否为空
      if (!croppedImage || croppedImage === 'data:,') {
        throw new Error('Generated image is invalid');
      }

      // 裁剪成功后，直接传递结果给父组件，不进行预加载验证
      onComplete(croppedImage);
    } catch (_error) {
      // Handle error silently
    } finally {
      onLoading(false);
    }
  };

  const handleRatioChange = (_event: Event, value: number | number[]) => {
    const newValue = typeof value === 'number' ? value : value[0];
    setSliderPosition(newValue);
    const newRatio = ASPECT_RATIOS[newValue].value;
    setAspectRatio(newRatio);

    // 当宽高比变化时，重置裁剪区域和缩放
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // 当用户调整缩放时
  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom);
  };

  // 旋转处理函数
  const handleRotateLeft = () => {
    // 逆时针旋转90度（减去90度）
    const newRotation = (rotation - 90) % 360;
    setRotation(newRotation);
  };

  const handleRotateRight = () => {
    // 顺时针旋转90度（加上90度）
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
  };

  // 渲染裁剪器内容
  const renderContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        pt: { xs: 1, sm: 2 },
        pb: { xs: 3, sm: 0 },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          ml: 1,
          mb: 1,
          fontWeight: 600,
          fontSize: { xs: '1rem', sm: '1.25rem' },
        }}
      >
        {t('crop.title')}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          height: isMobile ? 'calc(100vh - 350px)' : 'calc(100% - 180px)',
          minHeight: isMobile ? 250 : 300,
          bgcolor: 'transparent',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 1.5,
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            ...elegantTextureBackground,
            opacity: 0.3,
          },
          '& .reactEasyCrop_Container': {
            zIndex: 1,
            backgroundColor: 'transparent !important',
          },
          '& .reactEasyCrop_CropArea': {
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          rotation={rotation}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={handleZoomChange}
          onRotationChange={setRotation}
          cropShape="rect"
          showGrid={true}
          objectFit="contain"
        />
      </Box>

      {/* 控制面板 - 响应式布局 */}
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0, md: 2 }}>
          <Box sx={{ px: { xs: 1, md: 1 }, flex: { xs: 'none', md: 1 } }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
              }}
            >
              <span>{t('crop.controls.image_ratio')}</span>
              <span>{ASPECT_RATIOS[sliderPosition].label}</span>
            </Typography>
            <Slider
              value={sliderPosition}
              min={0}
              max={ASPECT_RATIOS.length - 1}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => ASPECT_RATIOS[value].label}
              onChange={handleRatioChange}
              marks={ASPECT_RATIOS.map((_, index) => ({ value: index }))}
              sx={{
                height: 4,
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  background: '#ffffff',
                  border: '2px solid #6a7efc',
                  '@media (hover: hover)': {
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0 3px 8px rgba(106,126,252,0.25)',
                    },
                  },
                },
                '& .MuiSlider-track': {
                  height: 4,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #879fff, #6a7efc)',
                },
                '& .MuiSlider-rail': {
                  height: 4,
                  borderRadius: 2,
                  opacity: 0.5,
                  background: 'rgba(220, 220, 230, 0.8)',
                  border: '1px solid rgba(200, 200, 210, 0.8)',
                },
                '& .MuiSlider-mark': {
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(180, 180, 190, 0.6)',
                  '&.MuiSlider-markActive': {
                    backgroundColor: '#6a7efc',
                  },
                },
                '& .MuiSlider-valueLabel': {
                  background: '#6a7efc',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  '&:before': {
                    display: 'none',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 0, md: 1 },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Stack spacing={1} direction="row" alignItems="center">
              <IconButton
                onClick={handleRotateLeft}
                size="small"
                sx={{
                  bgcolor: 'rgba(106,126,252,0.08)',
                  color: '#6a7efc',
                  '@media (hover: hover)': {
                    '&:hover': {
                      bgcolor: 'rgba(106,126,252,0.16)',
                    },
                  },
                  width: 32,
                  height: 32,
                }}
                title={t('crop.tooltips.rotateLeft')}
              >
                <MaterialSymbolsRotateLeftRounded height={18} width={18} />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  minWidth: 32,
                  textAlign: 'center',
                }}
              >
                {rotation}°
              </Typography>
              <IconButton
                onClick={handleRotateRight}
                size="small"
                sx={{
                  bgcolor: 'rgba(106,126,252,0.08)',
                  color: '#6a7efc',
                  '@media (hover: hover)': {
                    '&:hover': {
                      bgcolor: 'rgba(106,126,252,0.16)',
                    },
                  },
                  width: 32,
                  height: 32,
                }}
                title={t('crop.tooltips.rotateRight')}
              >
                <MaterialSymbolsRotateRightRounded height={18} width={18} />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ px: { xs: 1, md: 1 }, flex: { xs: 'none', md: 1 } }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
              }}
            >
              <span>{t('crop.controls.zoom')}</span>
              <span>{zoom.toFixed(1)}×</span>
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value.toFixed(1)}×`}
              onChange={(_e, value) => setZoom(value as number)}
              sx={{
                height: 4,
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  background: '#ffffff',
                  border: '2px solid #9370DB',
                  '@media (hover: hover)': {
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0 3px 8px rgba(147,112,219,0.25)',
                    },
                  },
                },
                '& .MuiSlider-track': {
                  height: 4,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #B19CD9, #9370DB)',
                },
                '& .MuiSlider-rail': {
                  height: 4,
                  borderRadius: 2,
                  opacity: 0.5,
                  background: 'rgba(220, 220, 230, 0.8)',
                  border: '1px solid rgba(200, 200, 210, 0.8)',
                },
                '& .MuiSlider-valueLabel': {
                  background: '#9370DB',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  '&:before': {
                    display: 'none',
                  },
                },
              }}
            />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 0.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{
              px: 2,
              py: 0.75,
              fontSize: '0.85rem',
              borderColor: 'rgba(106,126,252,0.3)',
              color: 'text.secondary',
              '@media (hover: hover)': {
                '&:hover': {
                  borderColor: 'rgba(106,126,252,0.6)',
                  backgroundColor: 'rgba(106,126,252,0.04)',
                },
              },
            }}
            size="small"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCropConfirm}
            sx={{
              px: 2,
              py: 0.75,
              fontSize: '0.85rem',
              backgroundColor: '#6a7efc',
              boxShadow: '0 2px 6px rgba(106,126,252,0.3)',
              '@media (hover: hover)': {
                '&:hover': {
                  backgroundColor: '#5a6fe6',
                  boxShadow: '0 4px 12px rgba(106,126,252,0.4)',
                },
              },
            }}
            size="small"
          >
            {t('common.save')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  // 根据设备类型选择不同的容器组件
  return isMobile ? (
    <BottomModalDrawer open={open} onClose={onClose}>
      {renderContent()}
    </BottomModalDrawer>
  ) : (
    <ModalTransitions
      open={open}
      onChange={(isOpen) => !isOpen && onClose()}
      contentSx={{
        width: '80vw',
        height: '88vh',
        display: 'flex',
        flexDirection: 'column',
        px: 3,
      }}
    >
      {renderContent()}
    </ModalTransitions>
  );
};

export default CropPanel;
